// components/views/sections/SkillsKPIsSection.js - VERSION OPTIMISÉE
import React from 'react';
import { GraduationCap, Grid, BarChart3 } from 'lucide-react';
import { useState } from 'react';
import { SkillsBarChart } from './SkillsBarChart';

export const SkillsKPIsSection = ({
    categories,
    completedSkills,
    getSkillsToImprove,
    skillsByDomain,
    skillsData = {}, // Données des compétences avec ratings
    customSkillDomains = [],
    setActiveView,
    // NOUVEAU : Props pour éviter les hooks et recalculs
    domainesData = [],
    skillsDataRaw = [],
    refreshTrigger // Trigger pour forcer le recalcul si nécessaire
}) => {
    // State pour gérer la vue actuelle
    const [viewMode, setViewMode] = useState('heatmap'); // 'heatmap' ou 'barchart'

    // Vérifications de sécurité pour éviter les erreurs
    const safeCategories = categories || [];
    const safeCompletedSkills = completedSkills || [];
    const safeSkillsByDomain = skillsByDomain || {};
    const safeSkillsData = skillsData || {};
    const safeCustomSkillDomains = customSkillDomains || [];

    // OPTIMISÉ : Utiliser les données brutes passées en props plutôt que de recalculer
    const safeDomainesData = domainesData || [];
    const safeSkillsDataRaw = skillsDataRaw || [];

    // Créer les domaines personnalisés au format category - GARDÉ POUR COMPATIBILITÉ
    const customCategories = safeCustomSkillDomains.map((domainName, index) => ({
        id: `custom-${index}`,
        name: domainName,
        icon: <GraduationCap className="w-3 h-3" />,
        isCustom: true
    }));

    // Combiner toutes les catégories
    const allCategories = [...safeCategories, ...customCategories];

    // OPTIMISÉ : Récupérer toutes les compétences avec leurs données - VERSION AMÉLIORÉE
    const getAllSkillsWithData = () => {
        const allSkills = [];

        // Si on a les données brutes, les utiliser directement
        if (safeSkillsDataRaw.length > 0) {
            safeSkillsDataRaw.forEach(skill => {
                // Trouver le domaine correspondant
                const domain = safeDomainesData.find(d => d.id_domaine === skill.id_domaine);
                const domainName = domain ? domain.nom_domaine : 'Domaine inconnu';
                const domainIcon = domain ? (domain.icon || <GraduationCap className="w-3 h-3" />) : <GraduationCap className="w-3 h-3" />;

                allSkills.push({
                    name: skill.nom_skill,
                    categoryId: skill.id_domaine,
                    categoryName: domainName,
                    categoryIcon: domainIcon,
                    rating: skill.rating || 0,
                    hours: skill.hours || 0,
                    certification: skill.certification || '',
                    trainingLink: skill.training_link || '',
                    notes: skill.notes || '',
                    isCompleted: safeCompletedSkills.some(cs => cs.name === skill.nom_skill || cs.id === skill.nom_skill),
                    // Score composite basé sur rating et complétion
                    score: skill.rating ? skill.rating * 20 : (safeCompletedSkills.some(cs => cs.name === skill.nom_skill || cs.id === skill.nom_skill) ? 100 : 10)
                });
            });
        } else {
            // Fallback vers l'ancienne méthode si les données brutes ne sont pas disponibles
            allCategories.forEach(category => {
                const domainSkills = safeSkillsByDomain[category.id] || [];
                domainSkills.forEach(skillName => {
                    const skillData = safeSkillsData[skillName] || {};
                    allSkills.push({
                        name: skillName,
                        categoryId: category.id,
                        categoryName: category.name,
                        categoryIcon: category.icon,
                        rating: skillData.rating || 0,
                        hours: skillData.hours || 0,
                        certification: skillData.certification || '',
                        trainingLink: skillData.trainingLink || '',
                        notes: skillData.notes || '',
                        isCompleted: safeCompletedSkills.some(cs => cs.name === skillName || cs.id === skillName),
                        // Score composite basé sur rating et complétion
                        score: skillData.rating ? skillData.rating * 20 : (safeCompletedSkills.some(cs => cs.name === skillName || cs.id === skillName) ? 100 : 10)
                    });
                });
            });
        }

        return allSkills;
    };

    // OPTIMISÉ : Calculer les KPIs des compétences avec mise en cache via refreshTrigger
    const calculateSkillsKPIs = () => {
        const allSkills = getAllSkillsWithData();

        const totalSkills = allSkills.length;
        const skillsWithRating = allSkills.filter(skill => skill.rating && skill.rating > 0);
        const skillsToWork = allSkills.filter(skill => skill.rating > 0 && skill.rating <= 2); // Rouge : rating 1-2
        const skillsMastered = allSkills.filter(skill => skill.rating >= 4 || skill.isCompleted); // Vert : rating 4-5 ou complété

        return {
            total: totalSkills,
            evaluated: skillsWithRating.length,
            toWork: skillsToWork.length,
            mastered: skillsMastered.length
        };
    };

    const skillsKPIs = calculateSkillsKPIs();
    const hasAnySkill = skillsKPIs.total > 0;

    // Composant Heatmap des compétences - INCHANGÉ
    const SkillsHeatmap = () => {
        const allSkills = getAllSkillsWithData();

        if (allSkills.length === 0) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center text-gray-500">
                        <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium mb-2">Aucune compétence définie</p>
                        <p className="text-sm mb-4">
                            Ajoutez des compétences aux domaines pour visualiser votre cartographie
                        </p>
                        <div className="text-xs text-gray-400">
                            La cartographie vous permettra de suivre votre progression et identifier vos priorités
                        </div>
                    </div>
                </div>
            );
        }

        // Trier les compétences par score décroissant
        const sortedSkills = allSkills.sort((a, b) => b.score - a.score);

        // Fonction pour calculer la disposition optimale
        const calculateLayout = (skills) => {
            const itemCount = skills.length;
            const containerAspectRatio = 16 / 9;

            // Calculer le nombre optimal de lignes et colonnes
            const cols = Math.ceil(Math.sqrt(itemCount * containerAspectRatio));
            const rows = Math.ceil(itemCount / cols);

            return skills.map((skill, index) => {
                const col = index % cols;
                const row = Math.floor(index / cols);

                // Calculer les dimensions en pourcentage
                const width = 100 / cols;
                const height = 100 / rows;

                return {
                    x: col * width,
                    y: row * height,
                    width: width - 0.2, // Petit espacement
                    height: height - 0.2,
                    data: skill
                };
            });
        };

        const layout = calculateLayout(sortedSkills);

        // Fonction pour obtenir la couleur basée sur le score/rating avec transparence légère
        const getColor = (skill, isHover = false) => {
            const opacity = isHover ? 0.85 : 1; // Accentuation au survol

            if (skill.isCompleted) {
                return `rgba(14, 148, 136, ${opacity})`; // Vert avec transparence légère pour les compétences complétées
            }

            if (skill.rating > 0) {
                // Basé sur le rating (1-5)
                if (skill.rating >= 4) return `rgba(14, 148, 136, ${opacity})`; // Vert avec transparence légère - Maîtrisé (4-5)
                if (skill.rating === 3) return `rgba(255, 173, 101, ${opacity})`; // Orange avec transparence légère - Bon (3)
                return `rgba(229, 122, 134, ${opacity})`; // Rouge avec transparence légère - À travailler (1-2)
            }

            // Pas de rating - gris avec transparence légère
            return `rgba(148, 163, 184, ${opacity})`; // Gris avec transparence légère - Non évalué
        };

        const getTextColor = (skill) => {
            return '#FFFFFF'; // Toujours blanc
        };

        // Fonction pour obtenir l'intensité (opacité) basée sur les heures de formation
        const getIntensity = (skill) => {
            if (!skill.hours) return 0.8;
            if (skill.hours >= 40) return 1;
            if (skill.hours >= 20) return 0.95;
            if (skill.hours >= 10) return 0.9;
            return 0.85;
        };

        return (
            <div className="relative w-full h-full rounded-lg overflow-hidden">
                {layout.map((rect, index) => {
                    const skill = rect.data;
                    const backgroundColor = getColor(skill);
                    const opacity = getIntensity(skill);

                    return (
                        <div
                            key={`${skill.categoryId}-${skill.name}`}
                            className="absolute border border-white transition-all hover:opacity-100 cursor-pointer flex flex-col justify-center items-center group"
                            style={{
                                left: `${rect.x}%`,
                                top: `${rect.y}%`,
                                width: `${rect.width}%`,
                                height: `${rect.height}%`,
                                backgroundColor: getColor(skill),
                                opacity: opacity,
                                color: getTextColor(skill)
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = getColor(skill, true);
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = getColor(skill, false);
                            }}
                            title={`${skill.name}\nDomaine: ${skill.categoryName}\nRating: ${skill.rating}/5${skill.hours ? `\nFormation: ${skill.hours}h` : ''}${skill.certification ? `\nCertification: ${skill.certification}` : ''}`}
                        >
                            <div className="text-center p-1 w-full h-full flex flex-col justify-center items-center">
                                <div className="text-xs mb-1">
                                    {React.isValidElement(skill.categoryIcon) ? (
                                        skill.categoryIcon
                                    ) : (
                                        <GraduationCap className="w-3 h-3" />
                                    )}
                                </div>
                                <div className="font-bold text-xs truncate w-full leading-tight px-1" style={{ fontSize: '10px' }}>
                                    {skill.name}
                                </div>

                                {/* Indicateur de rating ou statut */}
                                {skill.isCompleted ? (
                                    <div className="text-xs opacity-90 font-semibold">✓</div>
                                ) : skill.rating > 0 ? (
                                    <div className="text-xs opacity-90 font-semibold">{skill.rating}/5</div>
                                ) : (
                                    <div className="text-xs opacity-70">-</div>
                                )}

                                {/* Indicateurs supplémentaires */}
                                <div className="flex justify-center mt-1 space-x-1">
                                    {skill.certification && (
                                        <div className="w-1 h-1 rounded-full bg-yellow-300" title="Certification disponible"></div>
                                    )}
                                    {skill.trainingLink && (
                                        <div className="w-1 h-1 rounded-full bg-blue-300" title="Formation liée"></div>
                                    )}
                                    {skill.hours > 0 && (
                                        <div className="w-1 h-1 rounded-full bg-purple-300" title={`${skill.hours}h de formation`}></div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Légende simplifiée avec couleurs transparentes légères */}
                <div className="absolute bottom-2 right-2 bg-white bg-opacity-95 rounded px-2 py-1.5 text-xs shadow-sm border border-gray-200">
                    <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(148, 163, 184, 0.85)' }} title="Non évalué"></div>
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(229, 122, 134, 0.85)' }} title="À travailler (1-2/5)"></div>
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(255, 173, 101, 0.85)' }} title="Bon (3/5)"></div>
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(14, 148, 136, 0.85)' }} title="Maîtrisé (4-5/5)"></div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
            {/* Header avec sélecteur de vue */}
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 -m-4 mb-4 flex-shrink-0 rounded-t-xl">
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-700">
                        <h2 className="text-sm font-semibold">Cartographie des Compétences</h2>
                    </div>
                    <div className="flex items-center space-x-2">
                        {/* Sélecteur de vue */}
                        <div className="flex items-center bg-white rounded-lg border border-gray-200 p-1">
                            <button
                                onClick={() => setViewMode('heatmap')}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                                    viewMode === 'heatmap'
                                        ? 'bg-[#18574F] text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                title="Vue cartographie"
                            >
                                <Grid className="w-3 h-3" />
                                <span>Heatmap</span>
                            </button>
                            <button
                                onClick={() => setViewMode('barchart')}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                                    viewMode === 'barchart'
                                        ? 'bg-[#18574F] text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                title="Vue graphique en barres"
                            >
                                <BarChart3 className="w-3 h-3" />
                                <span>Barres</span>
                            </button>
                        </div>
                        
                        {skillsKPIs.total > 0 && (
                            <span className="bg-gray-200 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                                {skillsKPIs.total} compétence{skillsKPIs.total > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* KPIs des compétences */}
            {hasAnySkill && (
                <div className="grid grid-cols-4 gap-3 mb-4 flex-shrink-0">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-[#18574F]">
                            {skillsKPIs.total}
                        </div>
                        <div className="text-xs text-gray-600">Compétences</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-[#18574F]">
                            {skillsKPIs.evaluated}
                        </div>
                        <div className="text-xs text-gray-600">Évaluées</div>
                    </div>
                    <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'rgba(229, 122, 134, 0.1)' }}>
                        <div className="text-lg font-bold" style={{ color: '#e57a86' }}>
                            {skillsKPIs.toWork}
                        </div>
                        <div className="text-xs" style={{ color: '#e57a86' }}>À travailler</div>
                    </div>
                    <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'rgba(14, 148, 136, 0.1)' }}>
                        <div className="text-lg font-bold" style={{ color: '#0e9488' }}>
                            {skillsKPIs.mastered}
                        </div>
                        <div className="text-xs" style={{ color: '#0e9488' }}>Maîtrisées</div>
                    </div>
                </div>
            )}

            {/* Contenu principal - Vue conditionnelle */}
            <div className="flex-1 min-h-0">
                {viewMode === 'heatmap' ? (
                    <SkillsHeatmap />
                ) : (
                    <SkillsBarChart 
                        categories={safeCategories}
                        completedSkills={safeCompletedSkills}
                        skillsByDomain={safeSkillsByDomain}
                        skillsData={safeSkillsData}
                        customSkillDomains={safeCustomSkillDomains}
                        // NOUVEAU : Passer les données brutes pour éviter les recalculs
                        domainesData={safeDomainesData}
                        skillsDataRaw={safeSkillsDataRaw}
                    />
                )}
            </div>
        </div>
    );
};